<template>
  <div class="card" style="position:relative">
    <!-- Back button when seat map is displayed -->
    <b-button
      v-if="selectedMap"
      @click="selectedMap = null"
      style="position: absolute; top: 4%; left: 3%; z-index: 10;"
      type="is-dark"
      size="is-small"
      outlined
    >
      Back
    </b-button>

    <!-- Loading state -->
    <div class="box has-text-centered" v-if="loading">
      Requesting seat maps...
    </div>

    <!-- Selected seat map view -->
    <SeatMap
      v-else-if="selectedMap"
      :seatmap="selectedMap"
      :preview="preview"
      :passenger="passenger"
      @selected="selectSeat"
    ></SeatMap>

    <!-- Flight selection view -->
    <div class="box wdt" v-else>
      <!-- Header section -->
      <div class="mb-5">
        <button
          type="button"
          class="delete is-pulled-right"
          @click="$emit('close')"
        />
        <h1 class="is-size-3">Select flight</h1>
      </div>

      <!-- One-way flight view -->
      <div v-if="isOneWayFlight">
        <flight-leg-item
          v-for="flight in firstJourneyLegs"
          :key="`one-way-${flight.legIndex}`"
          :flight="flight"
          :preview="preview"
          :has-seat-map="hasSeatMap(0, flight.legIndex - 1)"
          :is-loading="isSeatMapLoading(0, flight.legIndex - 1)"
          @select-seat="goToSeatMap(findSegmentId(0, flight.legIndex - 1))"
        />
      </div>

      <!-- Multi-journey flight view with tabs -->
      <b-tabs v-else v-model="tab" type="is-boxed">
        <b-tab-item
          v-for="journey in journeys"
          :key="journey.uniqueKey"
          :label="journey.from + ' - ' + journey.to"
        >
          <flight-leg-item
            v-for="flight in journey.legs"
            :key="`multi-${journey.segmentIndex}-${flight.legIndex}`"
            :flight="flight"
            :preview="preview"
            :has-seat-map="hasSeatMap(journey.segmentIndex - 1, flight.legIndex - 1)"
            :is-loading="isSeatMapLoading(findSegmentId(journey.segmentIndex - 1, flight.legIndex - 1))"
            @select-seat="goToSeatMap(findSegmentId(journey.segmentIndex - 1, flight.legIndex - 1))"
          />
        </b-tab-item>
      </b-tabs>

      <!-- Close button for non-preview mode -->
      <div v-if="!preview" class="mt-6">
        <b-button
          class="is-block"
          style="margin: auto;"
          @click="$emit('close')"
          type="is-primary"
          expanded
        >
          Close Seat Viewer
        </b-button>
      </div>
    </div>
  </div>
</template>

<script>
import SeatMap from "./SeatMap.vue";
import FlightLegItem from "./FlightLegItem.vue";

export default {
  name: "SeatMapMenu",
  
  components: {
    SeatMap,
    FlightLegItem
  },
  
  props: {
    activeTab: Number,
    flight: {
      type: Object,
      required: true
    },
    flightProp: [Array, Object],
    preview: Boolean,
    guestIndex: Number
  },
  
  data() {
    return {
      seatMaps: [],
      loadStatus: [], // Loading status of each seat map
      tab: 1,
      selectedMap: null,
      loading: true,
      passenger: {
        id: 1,
        name: "",
        firstName: "",
        lastName: "",
        programs: []
      }
    };
  },
  
  computed: {
    journeys() {
      return this.flight.journeys || [];
    },
    
    isOneWayFlight() {
      return this.journeys.length === 1;
    },
    
    firstJourneyLegs() {
      return this.isOneWayFlight ? this.journeys[0].legs : [];
    },
    
    segmentMatrix() {
      const matrix = [];
      let index = 0;

      for (let j = 0; j < this.journeys.length; j++) {
        const journey = this.journeys[j];
        const legs = [];
        
        for (let l = 0; l < journey.legs.length; l++) {
          legs[l] = index++;
        }
        
        matrix[j] = legs;
      }
      
      return matrix;
    }
  },
  
  methods: {
    // Seat selection handling
    selectSeat(selectedSeat) {
      if (!this.selectedMap) return;
      
      const indices = this.findIndices(this.selectedMap);
      if (!indices) return;
      
      const [journeyIndex, legIndex] = indices;
      
      selectedSeat.journeyIndex = journeyIndex;
      selectedSeat.legIndex = legIndex;
      selectedSeat.responseId = this.selectedMap.responseId ?? null;
      selectedSeat.offerId = this.selectedMap.offerId ?? null;

      if (typeof this.guestIndex !== "undefined") {
        const payload = {
          guestIndex: this.guestIndex,
          selectedSeat
        };
        this.$store.commit("bookTravel/ADD_SEAT_GUEST", payload);
      } else {
        this.$store.commit("bookTravel/ADD_SEAT", selectedSeat);
      }

      this.selectedMap = null;
    },
    
    // Navigation to seat map
    goToSeatMap(segmentId) {
      if (segmentId === -1 || !this.seatMaps[segmentId]) return;
      this.selectedMap = this.seatMaps[segmentId];
    },
    
    // Check if seat map is available
    hasSeatMap(journeyIndex, legIndex) {
      // Validate indices
      if (!this.isValidIndex(journeyIndex, legIndex)) return false;
      
      const seatmap = this.getSeatMap(journeyIndex, legIndex);
      return Boolean(seatmap?.seatsGroupedByRow?.length);
    },
    
    // Get seat map for journey and leg
    getSeatMap(journeyIndex, legIndex) {
      if (!this.isValidIndex(journeyIndex, legIndex)) return null;
      
      const segmentId = this.findSegmentId(journeyIndex, legIndex);
      if (segmentId === -1) return null;
      
      return this.seatMaps.find(sMap => sMap?.segmentIndex === segmentId) || null;
    },
    
    // Find segment ID for journey and leg
    findSegmentId(journeyIndex, legIndex) {
      if (!this.isValidIndex(journeyIndex, legIndex)) return -1;
      return this.segmentMatrix[journeyIndex][legIndex];
    },
    
    // Find journey and leg indices for a seat map
    findIndices(seatMap) {
      if (!seatMap || typeof seatMap.segmentIndex === 'undefined') return null;
      
      const segmentIndex = seatMap.segmentIndex;
      let result = null;
      
      this.segmentMatrix.forEach((journey, j) => {
        journey.forEach((leg, l) => {
          if (leg === segmentIndex) {
            result = [j, l];
          }
        });
      });
      
      return result;
    },
    
    // Check if indices are valid
    isValidIndex(journeyIndex, legIndex) {
      return (
        journeyIndex >= 0 && 
        legIndex >= 0 && 
        this.segmentMatrix[journeyIndex] && 
        typeof this.segmentMatrix[journeyIndex][legIndex] !== 'undefined'
      );
    },
    
    // Check if seat map is currently loading
    isSeatMapLoading(segmentId) {
      return Boolean(this.loadStatus[segmentId]);
    },
    
    // Update loading status
    updateLoadingStatus(segmentId, isLoading) {
      this.$set(this.loadStatus, segmentId, isLoading);
    },
    
    // Build request for seat map
    buildRequest(journeyIndex, legIndex) {
      const journey = this.journeys[journeyIndex];
      const leg = journey.legs[legIndex];
      
      const data = {
        departureAirport: leg.departingAirport,
        arrivalAirport: leg.arrivingAirport,
        airlineCode: leg.marketingAirline.code,
        flightNumber: leg.flightNumber,
        departureDate: leg.departingAt.date,
        bookingClass: leg.fareInfo.bookingClass
      };
      
      const program = this.findFrequentFlyerProgram(data.airlineCode);
      if (program) {
        data.traveler = {
          firstName: this.passenger.firstName,
          lastName: this.passenger.lastName,
          frequentFlyer: {
            company: data.airlineCode,
            number: program
          }
        };
      }
      
      return data;
    },
    
    // Find frequent flyer program for airline
    findFrequentFlyerProgram(airlineCode) {
      if (!this.passenger.programs) return null;
      
      const program = this.passenger.programs.find(
        p => p.providerCode === airlineCode
      );
      
      return program ? program.accountNumber : null;
    },
    
    // Fetch all seat maps
    async getAllMaps() {
      this.loading = true;

      try {
        const requests = [];
        
        // Create requests for all journey legs
        this.segmentMatrix.forEach((journey, j) => {
          journey.forEach((segmentId, l) => {
            requests.push(this.fetchSeatMap(j, l, segmentId));
          });
        });
        
        // Wait for all requests to complete
        await Promise.all(requests);
      } catch (error) {
        console.error('Error fetching seat maps:', error);
      } finally {
        this.loading = false;
      }
    },
    
    // Fetch single seat map
    async fetchSeatMap(journeyIndex, legIndex, segmentId) {
      this.updateLoadingStatus(segmentId, true);
      
      try {
        const body = this.buildRequest(journeyIndex, legIndex);
        const result = await this.$app.huddleLabsServerCall("amadeus/air/seat-map", body);
        
        if (result?.seatsGroupedByRow) {
          const seatMap = {
            ...result,
            segmentIndex: segmentId
          };
          
          this.$set(this.seatMaps, segmentId, seatMap);
        }
      } catch (error) {
        console.error(`Error fetching seat map for J${journeyIndex}L${legIndex}:`, error);
      } finally {
        this.updateLoadingStatus(segmentId, false);
      }
    },
    
    // Fetch NDC seat maps (different API)
    async getNdcSeatmaps() {
      this.loading = true;
      
      try {
        const offer = this.flight.offer;
        if (!offer) return;
        
        const params = {
          meta: {
            stateful: false,
            normalizeResponse: true
          },
          ownerCode: offer.ownerCode,
          responseId: offer.responseId,
          offerId: offer.offerId
        };
        
        const result = await this.$app.huddleLabsServerCall(
          "amadeus/travel/seatmap",
          params
        );
        
        if (result?.data?.maps) {
          result.data.maps.forEach(seatMap => {
            const segmentId = seatMap.segmentIndex;
            this.$set(this.seatMaps, segmentId, {
              ...seatMap,
              responseId: result.data.responseId,
              offerId: result.data.offerId
            });
          });
        }
      } catch (error) {
        console.error('Error fetching NDC seat maps:', error);
      } finally {
        this.loading = false;
      }
    },
    
    // Initialize passenger information
    initializePassenger() {
      let profile;
      
      if (typeof this.guestIndex !== "undefined") {
        profile = this.$store.state.bookTravel.guests[this.guestIndex].profile;
      } else {
        profile = this.$store.getters["travellersAndTrips/getCurrentTravellerProfile"];
      }
      
      this.passenger = {
        ...this.passenger,
        programs: profile.travelerPrograms || [],
        name: `${profile.firstName} ${profile.lastName}`,
        firstName: profile.firstName,
        lastName: profile.lastName
      };
    }
  },
  
  async mounted() {
    this.tab = this.activeTab || 0;
    this.initializePassenger();
    
    // Fetch seat maps based on flight type
    if (this.flight.offer) {
      await this.getNdcSeatmaps();
    } else {
      await this.getAllMaps();
    }
  }
};
</script>

<style scoped>
.seatmap-container {
  display: inline-block;
  overflow-y: scroll;
  max-height: 400px;
}

@media screen and (max-width: 1023px) {
  .wdt {
    width: 100%;
    margin: auto;
  }
}

@media screen and (min-width: 1024px) {
  .wdt {
    width: 500px;
    margin: auto;
  }
}
</style>